import { useDateTimeDisplay } from '../../../../../platform/util/useDateTimeDisplay';
import { format } from 'date-fns';
import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CampaignIcon from "@mui/icons-material/Campaign";
import CancelSchedule from "./cancelSchedule";
import NotifyScheduleChange from "./notifyScheduleChange";
import { GroupBookingsByDateAndTimeRange } from "./groupBookingsByDateAndTimeRange";

const BookingRecord = ({
  currentYear,
  bookingType,
  bookingsArray,
  getBookingDetail,
  answersData,
  timezone,
  compact = false,
  initialBookingId = "",
  initialAction = "",
}) => {
  const { dateLabel, clockLabel } = useDateTimeDisplay();
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  // Global toggle for collapse/expand of all content
  const [showContent, setShowContent] = useState(() => !compact);

  // Group bookings using your existing logic then flatten to events
  const groupedBookings = GroupBookingsByDateAndTimeRange(
    bookingsArray,
    getBookingDetail,
    answersData,
    timezone
  );
  const events = [];
  Object.entries(groupedBookings).forEach(([dateKey, timeRanges]) => {
    Object.entries(timeRanges).forEach(([timeRangeKey, bookingTypes]) => {
      Object.entries(bookingTypes).forEach(([bookingTypeTitle, group]) => {
        group.bookings.forEach((item, idx) => {
          events.push({
            id: `${item.booking.id}-${idx}`,
            date: dateKey,
            timeRange: timeRangeKey,
            bookingType: bookingTypeTitle,
            color: group.color,
            booking: item.booking,
            answer: item.answer,
          });
        });
      });
    });
  });
  // Sort events by date
  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Group events by day, then time range, then booking type
  const eventsByDay = events.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = {};
    if (!acc[event.date][event.timeRange]) acc[event.date][event.timeRange] = {};
    if (!acc[event.date][event.timeRange][event.bookingType])
      acc[event.date][event.timeRange][event.bookingType] = [];
    acc[event.date][event.timeRange][event.bookingType].push(event);
    return acc;
  }, {});

  const today = new Date();

  // Format date with "Today" if applicable
  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    return dateLabel(format(date, 'yyyy-MM-dd')) + (isToday ? " - Today" : "");
  };

  // Toggle collapse for day group
  const toggleDayGroup = (dateKey) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey],
    }));
  };

  // Default expanded state for day groups: expand only today's day group.
  const defaultExpandedDays = {};
  Object.keys(eventsByDay).forEach((dateKey) => {
    const date = new Date(dateKey);
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    defaultExpandedDays[dateKey] = isToday;
  });
  const [expandedDays, setExpandedDays] = useState(defaultExpandedDays);

  // Toggle collapse for time group (within a day)
  const toggleTimeGroup = (dateKey, timeRange) => {
    const key = `${dateKey}-${timeRange}`;
    setExpandedTimeGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Default expanded state for time groups: for today's day, expand all.
  const defaultExpandedTimeGroups = {};
  Object.entries(eventsByDay).forEach(([dateKey, timeGroups]) => {
    const date = new Date(dateKey);
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    if (isToday) {
      Object.keys(timeGroups).forEach((timeRange) => {
        defaultExpandedTimeGroups[`${dateKey}-${timeRange}`] = true;
      });
    }
  });
  const [expandedTimeGroups, setExpandedTimeGroups] = useState(defaultExpandedTimeGroups);
  const [compactExpandedDays, setCompactExpandedDays] = useState({});

  // Cancel individual booking
  const handleCancelBooking = (selectedBooking) => {
    if (!selectedBooking) return;
    setSelectedBookings([selectedBooking]);
    setShowCancelModal(true);
  };

  const handleNotifyBooking = (selectedBooking) => {
    if (!selectedBooking) return;
    setSelectedBookings([selectedBooking]);
    setShowNotifyModal(true);
  };

  const compactSeedEvents =
    compact && bookingType === "upcoming" ? events.slice(0, 12) : [];
  const compactSeedByDay = compactSeedEvents.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = [];
    acc[event.date].push(event);
    return acc;
  }, {});
  const compactDayKeys = Object.keys(compactSeedByDay);
  const compactDayKeysSig = compactDayKeys.join("|");
  const compactTodayKey = compactDayKeys.find((key) => {
    const firstTime = compactSeedByDay[key]?.[0]?.booking?.time;
    if (!firstTime) return false;
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const eventStart = new Date(firstTime);
    eventStart.setHours(0, 0, 0, 0);
    return eventStart.getTime() === todayStart.getTime();
  });

  useEffect(() => {
    if (!(compact && bookingType === "upcoming")) return;
    const dayKeys = compactDayKeysSig ? compactDayKeysSig.split("|") : [];
    const preferredOpenKey = compactTodayKey || dayKeys[0];
    const next = {};
    dayKeys.forEach((key) => {
      next[key] = key === preferredOpenKey;
    });
    setCompactExpandedDays(next);
  }, [compact, bookingType, compactDayKeysSig, compactTodayKey]);

  const deepLinkedBooking = initialBookingId
    ? events.find((event) => Number(event?.booking?.id) === Number(initialBookingId))?.booking || null
    : null;

  useEffect(() => {
    if (deepLinkHandled || !initialBookingId) return;
    if (!deepLinkedBooking) return;

    setSelectedBookings([deepLinkedBooking]);
    if (initialAction === "cancel") {
      setShowCancelModal(true);
    } else if (initialAction === "reschedule" || initialAction === "notify") {
      setShowNotifyModal(true);
    }
    setDeepLinkHandled(true);
  }, [deepLinkHandled, deepLinkedBooking, initialAction, initialBookingId]);

  if (compact && bookingType === "upcoming") {
    const maxItems = 12;
    const compactEvents = events.slice(0, maxItems);
    const timeFormatter = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      ...(timezone ? { timeZone: timezone } : {}),
    });
    const formatCompactTimeRange = (booking, fallbackRange) => {
      const start = booking?.time ? new Date(booking.time) : null;
      if (!start || Number.isNaN(start.getTime())) return fallbackRange || "Time TBD";
      const durationMinutes = Math.max(1, Number(booking?.duration || 60));
      const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
      return `${timeFormatter.format(start)} - ${timeFormatter.format(end)}`;
    };
    const formatInviteeLabel = (rawValue) => {
      const value = String(rawValue || "").trim();
      if (!value) return "Attendee";
      const parts = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const nonEmailParts = parts.filter((v) => !v.includes("@"));

      if (parts.length > 1) {
        return `${parts.length} attendees`;
      }
      if (nonEmailParts.length === 1) {
        return nonEmailParts[0];
      }
      return "Attendee";
    };
    const formatCompactDayHeader = (dayEvents = [], fallbackDateKey = "") => {
      const firstEventTime = dayEvents?.[0]?.booking?.time;
      if (!firstEventTime) return { label: fallbackDateKey, hint: "" };

      const eventDate = new Date(firstEventTime);
      const dayLabel = dateLabel(eventDate, timezone);

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const tomorrowStart = new Date(todayStart);
      tomorrowStart.setDate(tomorrowStart.getDate() + 1);
      const eventStart = new Date(eventDate);
      eventStart.setHours(0, 0, 0, 0);

      if (eventStart.getTime() === todayStart.getTime()) {
        return { label: dayLabel, hint: "Today" };
      }
      if (eventStart.getTime() === tomorrowStart.getTime()) {
        return { label: dayLabel, hint: "Tomorrow" };
      }
      return { label: dayLabel, hint: "" };
    };
    const compactByDay = compactEvents.reduce((acc, event) => {
      if (!acc[event.date]) acc[event.date] = [];
      acc[event.date].push(event);
      return acc;
    }, {});
    const toggleCompactDay = (key) => {
      setCompactExpandedDays((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };
    const nextDayHeader = compactEvents.length
      ? formatCompactDayHeader([compactEvents[0]], compactEvents[0]?.date || "")
      : null;

    return (
      <Box sx={{ maxWidth: "100%", mx: "auto", mt: 0.75, px: 1.25 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1.25, cursor: "pointer" }}
          onClick={() => setShowContent(!showContent)}
        >
          <EventNoteIcon color="primary" />
          <Typography variant="subtitle2" flex={1} sx={{ fontWeight: 700 }}>
            Upcoming ({events.length})
          </Typography>
          {showContent ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Stack>
        {!showContent && nextDayHeader && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 0.75, mb: 1 }}>
            {nextDayHeader.hint ? (
              <Chip
                size="small"
                label={nextDayHeader.hint}
                sx={{ height: 18, fontSize: 10, bgcolor: "#eef2ff", color: "#1e3a8a" }}
              />
            ) : null}
            <Typography variant="subtitle1" sx={{ fontWeight: 400, lineHeight: 1.1 }}>
              {nextDayHeader.label}
            </Typography>
          </Stack>
        )}

        <Collapse in={showContent} timeout="auto" unmountOnExit>
          <Box sx={{ maxHeight: 420, overflowY: "auto", pr: 0.5 }}>
          {compactEvents.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "left", p: 1 }}>
              No upcoming records.
            </Typography>
          ) : (
            Object.entries(compactByDay).map(([dateKey, dayEvents]) => (
              <Box key={dateKey} sx={{ mb: 1.25 }}>
                {(() => {
                  const dayHeader = formatCompactDayHeader(dayEvents, dateKey);
                  return (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{ px: 0.75, mb: 0.75, cursor: "pointer" }}
                      onClick={() => toggleCompactDay(dateKey)}
                    >
                      {compactExpandedDays?.[dateKey] ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                      {dayHeader.hint ? (
                        <Chip
                          size="small"
                          label={dayHeader.hint}
                          sx={{ height: 18, fontSize: 10, bgcolor: "#eef2ff", color: "#1e3a8a" }}
                        />
                      ) : null}
                      <Typography
                        variant="subtitle2"
                        sx={{ display: "block", fontWeight: 400, color: "text.primary", lineHeight: 1.1 }}
                      >
                        {dayHeader.label}
                      </Typography>
                    </Stack>
                  );
                })()}
                <Collapse in={Boolean(compactExpandedDays?.[dateKey])} timeout="auto" unmountOnExit>
                <List dense disablePadding sx={{ pl: 3 }}>
                  {dayEvents.map((event) => {
                    const source = String(event?.booking?.source || "").toLowerCase();
                    const isGoogle = source.includes("google");
                    const meetingTitle =
                      event?.booking?.bookingTypeName ||
                      event?.bookingType ||
                      "Meeting";
                    const staffName =
                      event?.booking?.staffName ||
                      event?.booking?.staff?.user?.username ||
                      event?.booking?.staff?.name ||
                      "Staff";
                    const inviteeLabel = formatInviteeLabel(event?.booking?.inviteeName);
                    const timeRangeLabel = formatCompactTimeRange(event?.booking, event?.timeRange);
                    return (
                      <ListItem
                        key={event.id}
                        sx={{
                          py: 0.75,
                          px: 1,
                          border: "1px solid rgba(15,23,42,0.08)",
                          borderRadius: 1.5,
                          mb: 0.75,
                          backgroundColor: "background.paper",
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                  {meetingTitle}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Chip
                                    size="small"
                                    label={isGoogle ? "Google" : "Timely365"}
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      bgcolor: isGoogle ? "#e8f5e9" : "#e8f0fe",
                                      color: isGoogle ? "#2e7d32" : "#1d4ed8",
                                    }}
                                  />
                                  {!isGoogle && (
                                    <Stack direction="row" spacing={0.25} alignItems="center">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        aria-label="notify booking update"
                                        onClick={() => handleNotifyBooking(event.booking)}
                                        sx={{ p: 0.25 }}
                                      >
                                        <CampaignIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        aria-label="cancel booking"
                                        onClick={() => handleCancelBooking(event.booking)}
                                        sx={{ p: 0.25 }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  )}
                                </Stack>
                              </Stack>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800, lineHeight: 1.15, mt: 0.35, color: "#0f172a" }}
                              >
                                {clockLabel(timeRangeLabel)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.2 }}>
                                {inviteeLabel}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {staffName}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
                </Collapse>
              </Box>
            ))
          )}
          {events.length > maxItems && (
            <Typography variant="caption" sx={{ color: "text.secondary", px: 0.5 }}>
              Showing next {maxItems}. Adjust date range to view more.
            </Typography>
          )}
          </Box>
        </Collapse>
        <CancelSchedule
          currentData={selectedBookings}
          showCancelModal={showCancelModal}
          setShowCancelModal={setShowCancelModal}
          answersData={answersData}
        />
        <NotifyScheduleChange
          currentData={selectedBookings}
          showNotifyModal={showNotifyModal}
          setShowNotifyModal={setShowNotifyModal}
          initialChangeType={initialAction === "reschedule" ? "rescheduled" : "general"}
        />
      </Box>
    );
  }

  // Cancel all bookings for a given booking type group within a time range and day
  const handleCancelAllBookings = (dateKey, timeRange, bookingType) => {
    const bookingsGroup = eventsByDay[dateKey][timeRange][bookingType];
    const allBookings = bookingsGroup.map((e) => e.booking);
    setSelectedBookings(allBookings);
    setShowCancelModal(true);
  };

  const handleNotifyAllBookings = (dateKey, timeRange, bookingType) => {
    const bookingsGroup = eventsByDay[dateKey][timeRange][bookingType];
    const allBookings = bookingsGroup.map((e) => e.booking);
    setSelectedBookings(allBookings);
    setShowNotifyModal(true);
  };

  // BookingTypeHeader component: shows booking type name, number of attendees, and a menu for "Cancel All".
  const BookingTypeHeader = ({ dateKey, timeRange, bType, eventsList }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const handleMenuOpen = (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };
    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
        {/* Left side: Attendee Chip and booking type title */}
        <Stack direction="row" alignItems="center" spacing={1}>

          <Typography variant="body1" fontWeight="bold">
            {bType}
          </Typography>
          <Tooltip title={`Attendees: ${eventsList.length}`} arrow>
            <Chip
              label={eventsList.length}
              size="small"
              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontSize: "0.75rem" }}
            />
          </Tooltip>
        </Stack>
        {/* Right side: Menu icon */}
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
            handleNotifyAllBookings(dateKey, timeRange, bType);
          }}
        >
          Notify All About Change
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
              handleCancelAllBookings(dateKey, timeRange, bType);
            }}
          >
            Cancel All
          </MenuItem>
        </Menu>
      </Stack>
    );
  };

  return (
    <Box sx={{ maxWidth: compact ? "100%" : 700, mx: "auto", mt: compact ? 0.5 : 3, px: compact ? 1 : 2 }}>
      {/* Main Header with collapse/expand toggle */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: compact ? 1 : 2, cursor: "pointer" }}
        onClick={() => setShowContent(!showContent)}
      >
        <EventNoteIcon color="primary" />
        <Typography variant={compact ? "subtitle2" : "h6"} flex={1} sx={{ fontWeight: compact ? 700 : 500 }}>
          {bookingType.charAt(0).toUpperCase() + bookingType.slice(1)} appointments{compact ? "" : ` (${currentYear})`}
        </Typography>
        {showContent ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Stack>

      {/* Collapsible Content */}
      <Collapse in={showContent} timeout="auto" unmountOnExit>
        {Object.keys(eventsByDay).length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", p: 2 }}>
            No upcoming records.
          </Typography>
        ) : (
          Object.entries(eventsByDay).map(([dateKey, timeGroups]) => {
            // Unique event count for the day: count distinct booking type groups across all time ranges.
            const dayUnique = Object.values(timeGroups).reduce(
              (sum, timeGroup) => sum + Object.keys(timeGroup).length,
              0
            );
            return (
              <Box key={dateKey} sx={{ mb: 1 }}>
                {/* Day Header */}
                <Box
                  onClick={() => toggleDayGroup(dateKey)}
                  sx={{
                    bgcolor: "grey.200",
                    p: 1,
                    pl: 2,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" fontWeight="bold" fontSize="0.8rem">
                      {getFormattedDate(dateKey)}
                    </Typography>
                    <Tooltip title={`Events: ${dayUnique}`} arrow>
                    <Chip
                      label={`Total: ${dayUnique}`}
                      size="small"
                      sx={{ bgcolor: "#e0f7fa", color: "#00695c", fontSize: "0.75rem" }}
                    />
                    </Tooltip>
                  </Stack>
                  {/* {expandedDays[dateKey] ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )} */}
                </Box>
                <Collapse in={expandedDays[dateKey]} timeout="auto" unmountOnExit>
                  {Object.entries(timeGroups).map(([timeRange, bookingTypeGroups]) => {
                    // Unique event count for this time range: count distinct booking type groups.
                    const timeGroupUnique = Object.keys(bookingTypeGroups).length;
                    const timeGroupKey = `${dateKey}-${timeRange}`;
                    return (
                      <Box key={timeGroupKey} sx={{ pl: 2, pt: 0.5 }}>
                        {/* Time Range Header */}
                        <Box
                          onClick={() => toggleTimeGroup(dateKey, timeRange)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor: "grey.200",
                            fontSize: "1rem",
                            "&:hover": { bgcolor: "grey.50" },
                          }}
                        >
                          <Stack direction="row" alignItems="center">
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                              sx={{ fontSize: ".85rem" }}
                            >
                              {clockLabel(timeRange)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Tooltip title={`Events: ${timeGroupUnique}`} arrow>
                            <Chip
                              label={`${timeGroupUnique}`}
                              size="small"
                              sx={{ bgcolor: "#e0f7fa", color: "#00695c", fontSize: "0.75rem" }}
                              />
                            </Tooltip>
                            {/* {expandedTimeGroups[timeGroupKey] ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )} */}
                          </Stack>
                        </Box>
                        <Collapse in={expandedTimeGroups[timeGroupKey]} timeout="auto" unmountOnExit>
                          {Object.entries(bookingTypeGroups).map(([bType, eventsList]) => (
                            <Box key={`${timeGroupKey}-${bType}`} sx={{ pl: 3, pt: 0.3 }}>
                              {/* Booking Type Header with menu for Cancel All */}
                              <BookingTypeHeader
                                dateKey={dateKey}
                                timeRange={timeRange}
                                bType={bType}
                                eventsList={eventsList}
                              />
                              {/* Render individual events */}
                              <List>
                                {eventsList.map((event) =>
                                  event?.booking?.source !== "google" && (
                                    <ListItem
                                      key={event.id}
                                      sx={{ pl: 3, alignItems: "center", py: 1 }}
                                      secondaryAction={
                                        <Stack direction="row" spacing={0.5}>
                                          <IconButton
                                            edge="end"
                                            color="primary"
                                            onClick={() => handleNotifyBooking(event.booking)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <CampaignIcon />
                                          </IconButton>
                                          <IconButton
                                            edge="end"
                                            color="error"
                                            onClick={() => handleCancelBooking(event.booking)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <CloseIcon />
                                          </IconButton>
                                        </Stack>
                                      }
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            <strong>Name:</strong> {event.booking?.inviteeName || "N/A"}
                                          </Typography>
                                        }
                                        secondary={
                                          <Stack spacing={0.5}>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Email:</strong>{" "}
                                              <Box
                                                component="span"
                                                sx={{
                                                  display: "inline-block",
                                                  maxWidth: 180,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  verticalAlign: "bottom",
                                                }}
                                              >
                                                {event.booking?.inviteeEmail || "N/A"}
                                              </Box>
                                            </Typography>
                                            {event.booking.inviteePhoneNumbers &&
                                              event.booking.inviteePhoneNumbers.length > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                  <strong>Phone:</strong>{" "}
                                                  {event.booking.inviteePhoneNumbers.map((phone, index) => (
                                                    <React.Fragment key={phone}>
                                                      <a
                                                        href={`tel:${phone}`}
                                                        style={{ textDecoration: "none", color: "inherit" }}
                                                      >
                                                        {phone}
                                                      </a>
                                                      {index < event.booking.inviteePhoneNumbers.length - 1 ? ", " : ""}
                                                    </React.Fragment>
                                                  ))}
                                                </Typography>
                                              )}
                                            {event.answer && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontStyle: "italic" }}
                                              >
                                                {`Answer: ${event.answer}`}
                                              </Typography>
                                            )}
                                          </Stack>
                                        }
                                      />
                                    </ListItem>
                                  )
                                )}
                              </List>
                            </Box>
                          ))}
                        </Collapse>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })
        )}
        <CancelSchedule
          currentData={selectedBookings} 
          showCancelModal={showCancelModal}
          setShowCancelModal={setShowCancelModal}
          answersData={answersData}
        />
        <NotifyScheduleChange
          currentData={selectedBookings}
          showNotifyModal={showNotifyModal}
          setShowNotifyModal={setShowNotifyModal}
          initialChangeType={initialAction === "reschedule" ? "rescheduled" : "general"}
        />
      </Collapse>
    </Box>
  );
};

export default BookingRecord;
